import React, {
  useRef, useCallback, useMemo, RefObject, ReactElement, ForwardedRef,
} from 'react';
import { NativeTypes } from 'react-dnd-html5-backend';
import Image from '../image/image.component';
import FlexContainer from '../flex-container/flex-container.component';
import TextBlock from '../text-block/text-block.component';
import UploadFilePreviewItem from '../upload-file-preview-item/upload-file-preview-item.component';
import UploadFileButton from '../upload-file-button/upload-file-button.component';
import FormatService from '../../services/format-service';
import FileService from '../../services/file-service';
import FormService from '../../services/form-service';
import DateService from '../../services/date-service';
import useScreenSmall from '../../hooks/use-screen-small';
import useDropElement from '../../hooks/use-drop-element';
import useStylesUploadFile from './upload-file.styles';
import { FILE_EXTENSION_LIST_ALL } from '../../constants/system/file-extensions';
import NoImage from '../../static/images/no-image.png';

const UploadFile: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const ELEMENT_TYPE_FILE: string = NativeTypes.FILE;
  const INPUT_TYPE_FILE: string = 'file';
  const DRAG_FIELD_LABEL_ACTIVE: string = 'Release to drop';
  const DRAG_FIELD_LABEL_FILE_DEFAULT: string = 'Drop file here';
  const DRAG_FIELD_LABEL_FILES_DEFAULT: string = 'Drop files here';
  const DRAG_FIELD_LABEL_FOLDERS_DEFAULT: string = 'Drop folders here';

  const {
    isLight,
    isImage,
    isInline,
    isMultiple,
    isRecursive,
    isNarrow,
    isInvalid,
    isDisabled,
    isDroppable,
    isListImage,
    isSmallImage,
    isLargeImage,
    withDropOnly,
    withAllFilesAllowed,
    withDirectorySupport,
    withMultipleSelection,
    previewNameLimit,
    error,
    uploadFile,
    uploadFiles,
    fieldName,
    buttonLabel,
    acceptedExtensionList,
    defaultPreviewUrl,
    previewImageClassName,
    dropTargetBoxClassName,
    dropDeps,
    inputRefs,
    inputRefIndex,
    onFileChange,
    onFileDelete,
    onFilesChange,
    onDirectoriesChange,
    onError,
  } = props;

  const { classes } = useStylesUploadFile();
  const isSmallScreen: boolean = useScreenSmall();
  const uploadRef: RefObject<HTMLInputElement> | null = useRef(null);

  const onFileDrop: Function = useCallback(async (item: DropItem): Promise<void> => {
    if (isDroppable && item) {
      const droppedFiles: File[] | undefined = item.files;
      const droppedItems: FileSystemEntry[] | undefined = item.items && Array.from(item.items)
        .map((fileItem: DataTransferItem): FileSystemEntry | null => fileItem.webkitGetAsEntry())
        .filter((fileEntry: FileSystemEntry | null): boolean => !!fileEntry) as FileSystemEntry[];

      if (droppedFiles && droppedFiles.length && droppedItems && droppedItems.length) {
        if (isMultiple) {
          if (withDirectorySupport) {
            if (isRecursive) {
              await processDirectoryList(droppedItems);
            } else {
              const fileList: File[] = await processDirectoryFileList(droppedItems, processDirectoryEntry);

              processFileList(fileList);
            }
          } else {
            processFileList(droppedFiles);
          }
        } else {
          processFile(droppedFiles[0]);
        }
      }
    }
  }, [...[dropDeps || []]]);

  const {
    isCanBeDropped,
    isDropping,
    dropRef,
  } = useDropElement(
    ELEMENT_TYPE_FILE, null, isDisabled, onFileDrop,
  );

  const previewBlockInlineClassName: string | undefined = isInline ? classes.uploadFilePreviewBlockInline : undefined;
  const previewBlockMultipleClassName: string | undefined = isMultiple
    ? classes.uploadFilePreviewBlockMultiple : undefined;
  const containerUploadFileInlineClassName: string | undefined = isInline
    ? classes.containerUploadFileInline : undefined;
  const containerUploadFileRowClassName: string | undefined = isInline && isDroppable
    ? classes.containerUploadFileRow : undefined;
  const dropTargetBoxImageClassName: string | undefined = isImage ? classes.uploadFileDropTargetBoxImage : undefined;
  const dropTargetBoxInlineClassName: string | undefined = isInline && isDroppable
    ? classes.uploadFileDropTargetBoxInline : undefined;
  const dropTargetBoxActiveClassName: string | undefined = isCanBeDropped && isDropping
    ? classes.uploadFileDropTargetBoxActive : undefined;
  const dropTargetBoxListImageClassName: string | undefined = isListImage
    ? classes.uploadFileDropTargetBoxListImage : undefined;
  const dropTargetBoxSmallImageClassName: string | undefined = isSmallImage
    ? classes.uploadFileDropTargetBoxSmallImage : undefined;
  const dropTargetBoxLightClassName: string | undefined = isLight ? classes.uploadFileDropTargetBoxLight : undefined;
  const dropTargetBoxNarrowClassName: string | undefined = isNarrow ? classes.uploadFileDropTargetBoxNarrow : undefined;
  const previewImageListClassName: string | undefined = isListImage ? classes.uploadFilePreviewImageList : undefined;
  const previewImageSmallClassName: string | undefined = isSmallImage ? classes.uploadFilePreviewImageSmall : undefined;
  const previewImageLargeClassName: string | undefined = isLargeImage ? classes.uploadFilePreviewImageLarge : undefined;
  const previewImageResultClassName: string | undefined = FormatService.formatClassNames(
    classes.uploadFilePreviewImage, previewImageListClassName, previewImageSmallClassName, previewImageLargeClassName,
    previewImageClassName,
  );

  const dragFieldLabelDefault: string = useMemo((): string => {
    if (isMultiple) {
      if (withDirectorySupport && isRecursive) {
        return DRAG_FIELD_LABEL_FOLDERS_DEFAULT;
      }

      return DRAG_FIELD_LABEL_FILES_DEFAULT;
    }

    return DRAG_FIELD_LABEL_FILE_DEFAULT;
  }, [isMultiple, withDirectorySupport, isRecursive]);

  const allowedExtensionList: string[] = acceptedExtensionList || FILE_EXTENSION_LIST_ALL;
  const allowedExtensionsText: string = allowedExtensionList
    .map((extensionItem: string): string => extensionItem.toUpperCase()).join(', ');
  const allowedExtensionsAcceptText: string = allowedExtensionList
    ? allowedExtensionList.map((extensionItem: string): string => `.${extensionItem}`).join(', ') : '';
  const withFilesSelected: boolean = isMultiple ? uploadFiles && !!uploadFiles.length : !!uploadFile;
  const uploadFileUrl: string = uploadFile ? FileService.createFileLink(uploadFile) : (defaultPreviewUrl || NoImage);
  const withNoButtonOffset: boolean = isLargeImage;

  const onFileDeleteClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    fileIndex: number,
  ): void => {
    if (onFileDelete) {
      onFileDelete(event, fileIndex);
    }

    event.stopPropagation();
  }, [uploadFile, uploadFiles, ...[dropDeps || []]]);

  const checkFile: Function = useCallback((file: File): boolean => {
    const isFileValid: boolean = withAllFilesAllowed || FileService.checkFileExtension(file, allowedExtensionList);
    if (!isFileValid && onError) {
      onError('Incorrect file extension');
    }

    return isFileValid;
  }, [withAllFilesAllowed, allowedExtensionList]);

  const checkFileList: Function = useCallback((fileList: File[]): boolean => (
    fileList.reduce((result: boolean, file: File): boolean => result && file && checkFile(file), true)
  ), []);

  const processFile: Function = useCallback((file: File): void => {
    if (file && onFileChange && checkFile(file)) {
      onFileChange(file);
    }
  }, [uploadFile, uploadFiles]);

  const processFileList: Function = useCallback((fileList: File[]): void => {
    if (fileList && fileList.length) {
      if (onFilesChange && checkFileList(fileList)) {
        onFilesChange(fileList);
      } else if (onError) {
        if (allowedExtensionList && allowedExtensionList.length) {
          onError('Some files you are trying to upload are not supported. '
            + `Allowed extensions: ${allowedExtensionsText}`);
        } else {
          onError('Some files you are trying to upload are not supported');
        }
      }
    }
  }, [allowedExtensionList, ...[dropDeps || []]]);

  const fileFieldChange: ChangeHandlerFunction = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const eventTarget: HTMLInputElement = event.target;

    if (eventTarget.validity.valid) {
      if (isMultiple) {
        processFileList([...(eventTarget.files || [])]);
      } else {
        processFile(eventTarget.files && eventTarget.files[0]);
      }
    }
  }, [isMultiple, uploadFile, uploadFiles, withMultipleSelection]);

  const fileFieldClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    const eventTarget: HTMLInputElement = event.target as HTMLInputElement;

    eventTarget.value = '';
  }, [uploadFile, uploadFiles]);

  const processFileEntry: Function = useCallback((fileEntry: FileSystemFileEntry): Promise<ActionCallback> => (
    new Promise((resolve: ActionResolveCallback, reject: (error: DOMException) => void): void => {
      fileEntry.file(resolve, reject);
    })
  ), []);

  const processDirectoryFileList: Function = useCallback(async (
    fileEntries: FileSystemEntry[], processDirectoryCallback: Function,
  ): Promise<File[]> => {
    let fileList: File[];

    const fileListResults: Promise<File>[] = [];
    const directoryFileListResults: Promise<File[]>[] = [];

    if (fileEntries.length) {
      fileEntries.forEach((fileEntry: FileSystemEntry): void => {
        if (fileEntry.isDirectory && !fileEntry.isFile) {
          directoryFileListResults.push(processDirectoryCallback(fileEntry));
        } else {
          fileListResults.push(processFileEntry(fileEntry));
        }
      });
    }

    const directoryFileList: File[][] = directoryFileListResults && directoryFileListResults.length
      ? await Promise.all(directoryFileListResults) : [];

    fileList = fileListResults && fileListResults.length ? await Promise.all(fileListResults) : [];
    fileList = directoryFileList && directoryFileList.length ? [...fileList, ...directoryFileList.flat()] : fileList;

    return fileList;
  }, []);

  const processDirectoryEntry: Function = useCallback((
    directoryEntry: FileSystemDirectoryEntry,
  ): Promise<ActionCallback> => {
    const reader: FileSystemDirectoryReader = directoryEntry.createReader();

    return new Promise((resolve: ActionResolveCallback): void => {
      reader.readEntries(async (fileEntries: FileSystemEntry[]): Promise<void> => {
        const fileList: File[] = await processDirectoryFileList(fileEntries, processDirectoryEntry);

        resolve(fileList);
      });
    });
  }, []);

  const processDirectoryList: Function = useCallback(async (
    directoryEntries: FileSystemDirectoryEntry[],
  ): Promise<void> => {
    const isDirectoryListOnly: boolean = directoryEntries && directoryEntries.length
      ? directoryEntries.reduce((result: boolean, directoryEntry: FileSystemDirectoryEntry): boolean => (
        result && directoryEntry.isDirectory
      ), true) : true;

    if (isDirectoryListOnly) {
      const directoryNameList: string[] = [];
      const directoryFileResults: Promise<File[]>[] = [];
      directoryEntries.forEach((directoryEntry: FileSystemDirectoryEntry): void => {
        directoryNameList.push(directoryEntry.name);
        directoryFileResults.push(processDirectoryEntry(directoryEntry));
      });
      const directoryFileList: File[][] = await Promise.all(directoryFileResults);
      const groupedDirectoryFileList: DirectoryFileGroup[] = directoryNameList && directoryNameList.length
        ? directoryNameList.map((directoryName: string, directoryIndex: number): DirectoryFileGroup => ({
          name: directoryName,
          files: directoryFileList[directoryIndex] || [],
        })) : [];

      if (onDirectoriesChange) {
        onDirectoriesChange(groupedDirectoryFileList);
      }
    } else if (onError) {
      onError('Only folders are allowed here');
    }
  }, [...[dropDeps || []]]);

  if (isDisabled && isImage) {
    return (
      <Image
        isDefault
        text={buttonLabel}
        link={uploadFileUrl}
        className={previewImageResultClassName}
      />
    );
  }

  return (
    <FlexContainer
      isCentered={isInline}
      ref={ref}
      className={FormatService.formatClassNames(classes.containerUploadFile, containerUploadFileInlineClassName)}
    >
      <div className={containerUploadFileRowClassName}>
        {
          isDroppable && (!isSmallScreen || isImage) && (!withFilesSelected || withMultipleSelection) && (
            <div
              ref={dropRef}
              className={FormatService.formatClassNames(
                classes.uploadFileDropTargetBox, dropTargetBoxImageClassName, dropTargetBoxInlineClassName,
                dropTargetBoxActiveClassName, dropTargetBoxListImageClassName, dropTargetBoxSmallImageClassName,
                dropTargetBoxLightClassName, dropTargetBoxNarrowClassName, dropTargetBoxClassName,
              )}
            >
              {
                isImage && (
                  <UploadFileButton
                    isImage
                    withNoPadding
                    withNoOffset={withNoButtonOffset}
                    isDisabled={isDisabled}
                    uploadFile={uploadFile}
                    inputRefs={inputRefs}
                    inputRefIndex={inputRefIndex}
                    uploadRef={uploadRef}
                    buttonLabel={buttonLabel}
                    previewImageClassName={previewImageResultClassName}
                    uploadFileUrl={uploadFileUrl}
                    onFileDeleteClick={onFileDeleteClick}
                  />
                )
              }
              {
                !isImage && (
                  isCanBeDropped && isDropping ? DRAG_FIELD_LABEL_ACTIVE : dragFieldLabelDefault
                )
              }
            </div>
          )
        }
        {
          !withDropOnly && (
            <input
              multiple={isMultiple}
              ref={uploadRef}
              type={INPUT_TYPE_FILE}
              accept={allowedExtensionsAcceptText}
              name={fieldName}
              onClick={fileFieldClick}
              onChange={fileFieldChange}
            />
          )
        }
        {
          isInvalid && !!error && !withDropOnly && (
            <TextBlock className={classes.uploadFileError}>
              {error}
            </TextBlock>
          )
        }
        {
          ((isImage && (!isDroppable || (isDroppable && withFilesSelected)))
            || (!isImage && (!withFilesSelected || withMultipleSelection))) && !withDropOnly && (
            <UploadFileButton
              isImage={isImage}
              withNoOffset={withNoButtonOffset}
              isDisabled={isDisabled}
              uploadFile={uploadFile}
              inputRefs={inputRefs}
              inputRefIndex={inputRefIndex}
              uploadRef={uploadRef}
              buttonLabel={buttonLabel}
              previewImageClassName={previewImageResultClassName}
              uploadFileUrl={uploadFileUrl}
              onFileDeleteClick={onFileDeleteClick}
            />
          )
        }
      </div>
      {
        withFilesSelected && !isImage && !withDropOnly && (
          <div
            className={FormatService.formatClassNames(
              classes.uploadFilePreviewBlock, previewBlockInlineClassName, previewBlockMultipleClassName,
            )}
          >
            {
              isMultiple ? (
                uploadFiles.map((uploadFileItem: File | FileItem, uploadFileItemIndex: number): ReactElement => (
                  <div
                    key={`${(uploadFileItem as FileItem).id || FormService.FORM_KEY_NEW}-${uploadFileItem.name}-`
                      + `${(uploadFileItem as File).lastModified || DateService.getCurrentDateTimeStamp()}`}
                    className={classes.uploadFilePreviewFile}
                  >
                    <UploadFilePreviewItem
                      isInline={isInline}
                      uploadFile={uploadFileItem}
                      uploadFileIndex={uploadFileItemIndex}
                      previewNameLimit={previewNameLimit}
                      onFileDeleteClick={onFileDeleteClick}
                    />
                  </div>
                ))
              ) : (
                <UploadFilePreviewItem
                  isInline={isInline}
                  uploadFile={uploadFile}
                  previewNameLimit={previewNameLimit}
                  onFileDeleteClick={onFileDeleteClick}
                />
              )
            }
          </div>
        )
      }
    </FlexContainer>
  );
}));

export default UploadFile;
